import React, { useState, useContext, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthContext } from "../../contexts/auth";
import { AiOutlineClose, AiOutlineCheck, AiOutlineUser, AiOutlineMessage, AiOutlineClockCircle } from 'react-icons/ai';
import { FaWhatsapp, FaCheckDouble } from 'react-icons/fa';
import ConfirmDialog from "../ConfirmDialog";

// ============================================================================
// ANIMATIONS
// ============================================================================
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// ============================================================================
// STYLED COMPONENTS - DROPDOWN STYLE
// ============================================================================
const DropdownContainer = styled.div`
  position: fixed;
  z-index: 9999;
  width: 380px;
  max-height: 500px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.2),
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  animation: ${slideIn} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  /* Arrow pointing to button */
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 24px;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #318CD5 0%, #4facfe 100%);
    transform: rotate(45deg);
    border-radius: 3px;
  }

  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    max-height: 70vh;
    right: 10px !important;
  }
`;

const Header = styled.div`
  padding: 16px 20px;
  background: linear-gradient(135deg, #318CD5 0%, #4facfe 100%);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  backdrop-filter: blur(10px);

  svg {
    font-size: 18px;
    color: white;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  margin-left: 8px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 11px;
  font-size: 12px;
  font-weight: 700;
  color: white;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 16px;
    color: white;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  max-height: 350px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #318CD5;
    border-radius: 3px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 16px;
  background: linear-gradient(145deg, #f0fdfa, #ccfbf1);
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;

  svg {
    font-size: 28px;
    color: #318CD5;
  }
`;

const EmptyTitle = styled.p`
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  max-width: 240px;
  line-height: 1.4;
`;

const AtendimentosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AtendimentoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #318CD5;
    box-shadow: 0 2px 8px rgba(49, 140, 213, 0.12);
  }
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const CustomerAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #318CD5, #4facfe);
  border-radius: 10px;
  flex-shrink: 0;

  svg {
    font-size: 16px;
    color: white;
  }
`;

const CustomerDetails = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 12px;
    color: #6b7280;
  }
`;

const MessageBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  padding: 10px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;

  svg {
    flex-shrink: 0;
    font-size: 14px;
    color: #318CD5;
    margin-top: 2px;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #374151;
    line-height: 1.4;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 12px;

  svg {
    font-size: 12px;
    color: #9ca3af;
  }

  small {
    font-size: 11px;
    color: #9ca3af;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${({ $variant }) => {
    if ($variant === 'whatsapp') return 'linear-gradient(135deg, #25D366, #128C7E)';
    if ($variant === 'resolved') return 'linear-gradient(135deg, #10b981, #059669)';
    return 'linear-gradient(135deg, #318CD5, #4facfe)';
  }};
  color: white;
  font-size: 12px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${({ $variant }) => {
      if ($variant === 'whatsapp') return 'rgba(37, 211, 102, 0.35)';
      if ($variant === 'resolved') return 'rgba(16, 185, 129, 0.35)';
      return 'rgba(49, 140, 213, 0.35)';
    }};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const ResolveAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  background: white;
  color: #318CD5;
  font-size: 13px;
  font-weight: 600;
  border: 1.5px solid #318CD5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 15px;
  }

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #318CD5, #4facfe);
    color: white;
    border-color: transparent;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// ============================================================================
// BACKDROP
// ============================================================================
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
`;

// ============================================================================
// COMPONENT
// ============================================================================
const AtendimentoModal = ({
  isOpen,
  onClose,
  atendimentosPendentes = [],
  cancelarTodosAtendimentos,
  cancelarAtendimento,
  isLoading: loading = false,
  iniciarAtendimento,
  buttonRef
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const dropdownRef = useRef(null);

  const [atendimentoIniciando, setAtendimentoIniciando] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmResolverTodosOpen, setConfirmResolverTodosOpen] = useState(false);
  const [atendimentoParaRemover, setAtendimentoParaRemover] = useState(null);
  const [resolvendoTodos, setResolvendoTodos] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0 });

  // Calculate position based on button
  useEffect(() => {
    if (isOpen && buttonRef?.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 380;
      const windowWidth = window.innerWidth;

      let top = buttonRect.bottom + 12;
      let right = windowWidth - buttonRect.right;

      // Adjust if going off screen
      if (right + dropdownWidth > windowWidth - 20) {
        right = 10;
      }
      if (right < 10) {
        right = 10;
      }

      setPosition({ top, right });
    }
  }, [isOpen, buttonRef]);

  // ====================================================================
  // HANDLERS
  // ====================================================================
  const handleConfirmarRemocao = (atendimento) => {
    setAtendimentoParaRemover(atendimento);
    setConfirmDialogOpen(true);
  };

  const executarRemocao = () => {
    if (atendimentoParaRemover) {
      setConfirmDialogOpen(false);
      cancelarAtendimento(atendimentoParaRemover.atendimento_id, 'Resolvido pelo usuário');
      setAtendimentoParaRemover(null);
    }
  };

  const handleResolverTodos = async () => {
    console.log('[RESOLVER-TODOS-FRONT] handleResolverTodos chamado, pendentes:', atendimentosPendentes.length);
    if (atendimentosPendentes.length === 0) {
      console.log('[RESOLVER-TODOS-FRONT] Lista vazia, retornando sem fazer nada');
      return;
    }
    setConfirmResolverTodosOpen(false);
    setResolvendoTodos(true);

    try {
      console.log('[RESOLVER-TODOS-FRONT] Chamando cancelarTodosAtendimentos...');
      const resultado = await cancelarTodosAtendimentos();
      console.log('[RESOLVER-TODOS-FRONT] Resultado:', resultado);

      if (resultado.sucesso) {
        toast.success(t('support.allResolved', { count: resultado.total }));
      } else if (resultado.cancelados > 0) {
        toast.warning(t('support.partialResolved', {
          resolved: resultado.cancelados,
          total: resultado.total,
          failed: resultado.falharam
        }));
      } else {
        toast.error(t('support.resolveError'));
      }
    } catch (error) {
      console.error('[RESOLVER-TODOS-FRONT] Erro:', error);
      toast.error(t('support.resolveError'));
    } finally {
      setResolvendoTodos(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // ====================================================================
  // RENDER
  // ====================================================================
  if (!isOpen) return null;

  return createPortal(
    <>
      <Backdrop onClick={handleClose} />
      <DropdownContainer
        ref={dropdownRef}
        style={{ top: `${position.top}px`, right: `${position.right}px` }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <HeaderLeft>
            <HeaderIcon>
              <AiOutlineUser />
            </HeaderIcon>
            <Title>
              {t('support.title')}
              {atendimentosPendentes.length > 0 && (
                <Badge>{atendimentosPendentes.length}</Badge>
              )}
            </Title>
          </HeaderLeft>
          <CloseButton onClick={handleClose} aria-label="Close">
            <AiOutlineClose />
          </CloseButton>
        </Header>

        <Content>
          {atendimentosPendentes.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <AiOutlineCheck />
              </EmptyIcon>
              <EmptyTitle>{t('support.noPending')}</EmptyTitle>
              <EmptyHint>{t('support.noPendingHint')}</EmptyHint>
            </EmptyState>
          ) : (
            <AtendimentosList>
              {atendimentosPendentes.map((atendimento) => {
                const telefone = atendimento.celular?.split("@")[0] || atendimento.celular || "";
                const telefoneFormatado = telefone
                  ? `+${telefone.slice(0, 2)} ${telefone.slice(2, 4)} ${telefone.slice(4, 8)}-${telefone.slice(8)}`
                  : t('support.phoneNotProvided');

                const handleIniciarAtendimento = async () => {
                  if (!user) {
                    toast.error(t('support.userDataError'));
                    return;
                  }

                  setAtendimentoIniciando(atendimento.atendimento_id);

                  try {
                    const sucesso = await iniciarAtendimento(atendimento.atendimento_id, {
                      user_id: user._id,
                      user_name: user.name
                    });

                    if (sucesso) {
                      handleClose();
                      navigate("/whatsapp", {
                        state: {
                          lead_id: atendimento.lead_id,
                          nome: atendimento.nome || atendimento.nome_lead,
                          celular: atendimento.celular?.split('@')[0],
                          mensagem: atendimento.mensagem || atendimento.mensagem_lead,
                          timestamp: atendimento.timestamp || atendimento.createdAt
                        }
                      });
                    }
                  } catch (error) {
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao iniciar atendimento:', error);
                  } finally {
                    setAtendimentoIniciando(null);
                  }
                };

                const isIniciando = atendimentoIniciando === atendimento.atendimento_id;

                return (
                  <AtendimentoCard key={atendimento.atendimento_id}>
                    <CustomerInfo>
                      <CustomerAvatar>
                        <AiOutlineUser />
                      </CustomerAvatar>
                      <CustomerDetails>
                        <strong>{atendimento.nome || atendimento.nome_lead || t('support.customer')}</strong>
                        <small>{telefoneFormatado}</small>
                      </CustomerDetails>
                    </CustomerInfo>

                    <MessageBox>
                      <AiOutlineMessage />
                      <p>{atendimento.mensagem || atendimento.mensagem_lead || t('support.noMessage')}</p>
                    </MessageBox>

                    <TimeInfo>
                      <AiOutlineClockCircle />
                      <small>{new Date(atendimento.timestamp || atendimento.createdAt).toLocaleString()}</small>
                    </TimeInfo>

                    <ButtonGroup>
                      <ActionButton
                        $variant="whatsapp"
                        onClick={handleIniciarAtendimento}
                        disabled={isIniciando}
                      >
                        <FaWhatsapp />
                        {isIniciando ? t('support.starting') : t('support.replyWhatsApp')}
                      </ActionButton>
                      <ActionButton
                        $variant="resolved"
                        onClick={() => handleConfirmarRemocao(atendimento)}
                        disabled={isIniciando}
                      >
                        <AiOutlineCheck />
                        {t('support.resolved')}
                      </ActionButton>
                    </ButtonGroup>
                  </AtendimentoCard>
                );
              })}
            </AtendimentosList>
          )}
        </Content>

        {atendimentosPendentes.length > 0 && (
          <Footer>
            <ResolveAllButton
              onClick={() => setConfirmResolverTodosOpen(true)}
              disabled={resolvendoTodos}
            >
              <FaCheckDouble />
              {resolvendoTodos ? t('support.resolving') : t('support.markAllResolved')}
            </ResolveAllButton>
          </Footer>
        )}
      </DropdownContainer>

      {/* Confirmation Dialog - Individual */}
      <ConfirmDialog
        title={t('support.confirmResolution')}
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        onConfirm={executarRemocao}
      >
        <p>{t('support.confirmResolutionMessage')}</p>
        {atendimentoParaRemover && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f0fdfa',
            borderRadius: '8px',
            border: '1px solid #99f6e4'
          }}>
            <strong>{t('support.customer')}:</strong> {atendimentoParaRemover.nome || atendimentoParaRemover.nome_lead}
          </div>
        )}
      </ConfirmDialog>

      {/* Confirmation Dialog - Resolve All */}
      <ConfirmDialog
        title={t('support.resolveAll')}
        open={confirmResolverTodosOpen}
        setOpen={setConfirmResolverTodosOpen}
        onConfirm={handleResolverTodos}
      >
        <p>{t('support.resolveAllMessage', { count: atendimentosPendentes.length })}</p>
        <div style={{
          marginTop: '12px',
          padding: '12px',
          backgroundColor: '#fef3c7',
          borderRadius: '8px',
          border: '1px solid #fcd34d'
        }}>
          <strong>{t('status.warning')}:</strong> {t('support.resolveAllWarning')}
        </div>
      </ConfirmDialog>
    </>,
    document.body
  );
};

export default AtendimentoModal;
